import React, {
	ReactNode,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import clsx from "clsx";
import { Transition } from "react-transition-group";
import BaseModal from "@mui/material/Modal";

import * as Utils from "../../../lib/util/Utils";
import * as ReactUtils from "../../../lib/util/ReactUtils";

import ErrorPanel from "../../common/ErrorPanel/ErrorPanel";
import ErrorBoundary from "../../util/ErrorBoundary/ErrorBoundary";
import IconButton from "../../common/IconButton/IconButton";
import { AboveNavContext } from "../../nav/Navigation/Navigation";
import { ViewContext, VisibleContext } from "../../../lib/util/ReactUtils";
import { IconClose } from "../../../assets";

export interface ModalProps {
	visible?: boolean;
	onVisibleChange?: (visible: boolean) => void;
	onKeyDown?: (event: React.KeyboardEvent) => void;
	className?: string;
	closeOnEsc?: boolean;
	showCloseButton?: boolean;
	children?: ReactNode;
	dynamicHeight?: boolean;
	padding?: boolean;
	onClose?: (e: any) => void;
	onFinishedClose?: () => void;
	onFinishedOpen?: () => void;
	closeMaskOnClick?: boolean;
	scrollable?: boolean;
	forwardRef?:
		| React.MutableRefObject<HTMLDivElement>
		| ((current: HTMLDivElement) => void);
	visibleInExport?: boolean;
}

let scrollBarWidth: number | null = null;

const Modal = (props: ModalProps) => {
	const {
		visible,
		children,
		className,
		closeOnEsc,
		showCloseButton,
		dynamicHeight,
		padding,
		onKeyDown,
		onVisibleChange,
		closeMaskOnClick,
		onClose,
		onFinishedClose,
		onFinishedOpen,
		scrollable,
		forwardRef,
		visibleInExport,
	} = props;
	const [visibleIter, setVisibleIter] = useState(0);
	const [closing, setClosing] = useState(false);
	const shown = useRef(false);
	const visibleRef = useRef(visible);
	visibleRef.current = visible;

	const modalRef = useRef<HTMLDivElement>();
	const errorRef = useRef<Error>();

	const handleClosing = useCallback(() => {
		ReactUtils.getRootElement().inert = false;
		setClosing(true);
	}, []);

	const handleOpenAndClosed = useCallback(() => {
		setClosing(false);
	}, []);

	const handleEnter = useCallback(() => {
		if (onVisibleChange) {
			onVisibleChange(true);
		}
		ReactUtils.getRootElement().inert = true;
		if (!scrollBarWidth) {
			Utils.nextFrame(() => {
				scrollBarWidth = parseInt(
					document.body.style.paddingRight.replace(/px/, ""),
				);
			});
		}
	}, [onVisibleChange]);

	useEffect(() => {
		if (visible) {
			setClosing(false);
			setVisibleIter((visibleIter) => visibleIter + 1);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [Boolean(visible)]);

	if (visible) {
		shown.current = true;
	}

	const handleModalRef = useCallback((modal: HTMLDivElement) => {
		// kill the modal's own focus detection
		modalRef.current = modal;
		if (modal) {
			(modal.firstChild as HTMLDivElement).tabIndex = -1;
			(modal.lastChild as HTMLDivElement).tabIndex = -1;
		}
	}, []);

	useLayoutEffect(() => {
		if (modalRef.current) {
			handleModalRef(modalRef.current);
		}
	});

	const currentlyVisible = Boolean(closing || visible);

	const backdropProps = useMemo(
		() => ({
			className: clsx(
				"Modal__backdrop",
				visible && "--visible",
				closing && "--closing",
			),
			open: currentlyVisible,
		}),
		[closing, currentlyVisible, visible],
	);
	const style = useMemo(
		() =>
			closing
				? {
						paddingLeft: `${scrollBarWidth}px`,
				  }
				: undefined,
		[closing],
	);

	const handleClose = useCallback<any>(
		(e: any) => {
			if (onVisibleChange) {
				onVisibleChange(false);
			}
			if (onClose) {
				onClose(e);
			}
			if (onFinishedClose) {
				setTimeout(onFinishedClose, 200);
			}
		},
		[onVisibleChange, onClose, onFinishedClose],
	);

	useEffect(() => {
		return () => {
			// make sure we un-inert if this element is destroyed
			if (visibleRef.current) {
				ReactUtils.getRootElement().inert = false;
			}
		};
	}, []);

	const refHandle = useCallback(
		() => modalRef.current?.querySelector(".Modal__dialog") || document.body,
		[],
	);

	return shown.current ? (
		<ErrorBoundary
			resetKey={visibleIter}
			tag={"div"}
			role={"none"}
			errorRef={errorRef}
			className={"Modal__container"}
			onClick={Utils.stopPropagation}
		>
			{(error: Error | null): JSX.Element => (
				<BaseModal
					onKeyDown={onKeyDown}
					className={clsx(
						"Modal",
						className,
						visible && "--visible",
						error && "--error",
						closing && "--closing",
						!visibleInExport && "--pdf-ignore",
					)}
					disableEnforceFocus={true}
					disableEscapeKeyDown={closeOnEsc === false}
					ref={handleModalRef}
					onClose={handleClose}
					style={style}
					BackdropProps={backdropProps}
					open={Boolean(visible)}
					keepMounted={true}
				>
					<div
						ref={forwardRef || null}
						className={clsx(
							"Modal__dialog",
							dynamicHeight && "--dynamicHeight",
							padding && "--padding",
							visible && "--visible",
							closing && "--closing",
							scrollable && "--scrollable",
						)}
					>
						<ViewContext.Provider value={refHandle}>
							<Transition
								appear={true}
								onEnter={handleEnter}
								onExit={handleClosing}
								onExited={handleOpenAndClosed}
								onEntered={onFinishedOpen}
								in={visible}
								timeout={500}
							>
								{currentlyVisible ? (
									<AboveNavContext.Provider value={true}>
										<VisibleContext.Provider value={visible as any}>
											{error ? <ErrorPanel error={error} /> : children}
											{(showCloseButton !== false || error) && (
												<IconButton
													className={"Modal__close --pdf-ignore"}
													onClick={handleClose}
												>
													<IconClose />
												</IconButton>
											)}
										</VisibleContext.Provider>
									</AboveNavContext.Provider>
								) : (
									<div />
								)}
							</Transition>
						</ViewContext.Provider>
					</div>
				</BaseModal>
			)}
		</ErrorBoundary>
	) : null;
};

export default React.memo(Modal);
